import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { api } from '../services/api';

/**
 * What the API answers with, whichever way somebody signed in.
 *
 * One shape for both doors, deliberately: a session obtained from an emailed link is not a lesser
 * session than one obtained with a password, and giving it its own type would invite code that
 * treats it as one.
 */
interface AuthenticatedSession {
  user: any;
  token: string;
  tenantId?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, pass: string) => Promise<void>;
  signInWithLink: (signInToken: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  };

  useEffect(() => {
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('authUser');
      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        // Validate that the stored user object has the expected nested structure.
        if (parsedUser && parsedUser.personalInfo && parsedUser.personalInfo.firstName) {
          setToken(storedToken);
          setUser(parsedUser);
        } else {
          // The data in localStorage is corrupted or in an old format.
          logout();
        }
      }
    } catch (error) {
      console.error("Failed to parse auth data from localStorage", error);
      logout(); // Clear corrupted data on parsing error
    } finally {
        setLoading(false);
    }
  }, []);

  // Listen for global auth expiration signals (dispatched by lower-level services).
  useEffect(() => {
    const handler = () => {
      // Ensure we clear local state and storage, then navigate to login.
      try {
        logout();
      } catch (e) {
        console.error('Error during centralized logout handling', e);
      }

      try {
        if (typeof window !== 'undefined' && window.location) {
          window.location.hash = '#/login';
        }
      } catch (e) {
        // fallback noop
      }
    };

    window.addEventListener('auth:expired', handler as EventListener);
    return () => {
      window.removeEventListener('auth:expired', handler as EventListener);
    };
  }, []);

  const login = async (email: string, password: string) => {
    const resp = await api.post<AuthenticatedSession>('/api/users/authenticate/global', {
      email,
      password,
    });

    adoptSession(resp);
  };

  /**
   * Sign in with a link that was emailed, rather than with a password.
   *
   * The only way in for a merchant whose account the WordPress plugin created for them: they chose
   * no password and were never shown one. The backend answers with the SAME shape as a password
   * login, which is why this is four lines — the session it produces is not a different kind of
   * session, and treating it as one is how the two paths would drift apart.
   */
  const signInWithLink = async (signInToken: string) => {
    const resp = await api.post<AuthenticatedSession>('/api/auth/sign-in-link/redeem', {
      token: signInToken,
    });

    adoptSession(resp);
  };

  /**
   * Take a session the API just handed us and become signed in.
   *
   * Shared by both ways in on purpose. Everything here — the shape check, the tenant fallback, what
   * lands in storage — is a decision that must be identical whichever door was used, and the surest
   * way to keep it identical is to have written it once.
   */
  const adoptSession = (resp: AuthenticatedSession) => {
    if (!resp || !resp.user || !resp.token) {
      throw new Error('Authentication failed or invalid response from API.');
    }

    const { user: respUser, token: authToken, tenantId } = resp;

    // Fallback to 'default' if tenantId is missing
    const finalTenantId = tenantId || 'default';
    if (!tenantId) {
      console.warn('Login response missing tenantId, falling back to "default"');
    }

    const userData: User = {
      userId: respUser.userId,
      type: respUser.type,
      status: respUser.status,
      personalInfo: respUser.personalInfo,
      tenantId: finalTenantId,
    };

    if (!authToken || !userData.userId || !userData.personalInfo?.firstName) {
      throw new Error('Invalid data received from authentication API.');
    }

    localStorage.setItem('authToken', authToken);
    localStorage.setItem('authUser', JSON.stringify(userData));
    setUser(userData);
    setToken(authToken);
  };


  if (loading) {
      return (
          <div className="flex items-center justify-center h-screen bg-background">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
      );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, signInWithLink, logout, loading: false }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};